<template>
  <div ref="componentRef" class="min-h-screen bg-white">
    <!-- Search Bar Container - Positioned to align with navbar -->
    <div class="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
      <div class="flex justify-end mb-2">
        <div class="flex items-center border-b-2 border-gray-300 max-w-sm w-full">
          <input type="text" v-model="searchQuery" placeholder="Search careers by title, department, or location"
            @keydown.enter="handleSearch" @input="handleSearch"
            class="flex-1 bg-transparent text-primary placeholder-gray-400 border-none outline-none text-sm font-body mr-3 text-left" />
          <button v-if="searchQuery.trim()" @click="clearSearch"
            class="p-1.5 text-gray-700 hover:text-gray-900 transition-colors flex-shrink-0" aria-label="Clear search">
            <svg class="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <button @click="handleSearch"
            class="p-1.5 bg-accent text-white hover:bg-green-700 transition-colors flex-shrink-0" aria-label="Search">
            <svg class="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Main Content Container -->
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div v-if="!searchQuery.trim()">
        <div class="text-center mb-2.5">
          <h1 class="font-header text-[3rem] md:text-[3rem] font-bold text-primary mb-1.5">
            Careers
          </h1>
          <div v-if="pageDescription"
            class="font-body text-[1.125rem] text-gray-700 max-w-4xl mx-auto leading-[1.75] content-page-description"
            v-html="pageDescription">
          </div>
        </div>

        <div v-if="pageSections.length > 0" class="mb-0 space-y-[0.39rem]">
          <div v-for="(section, index) in pageSections" :key="index" class="text-left">
            <h2 v-if="section.title" class="font-header text-[1.5rem] xl:text-[1.5rem] font-bold text-primary mb-1">
              {{ section.title }}
            </h2>
            <div v-if="section.content"
              class="font-body text-[1.125rem] text-gray-900 leading-[1.75] content-page-section"
              v-html="section.content"></div>
          </div>
        </div>
      </div>

      <div class="prose max-w-none">

        <div v-if="submissionSuccess" class="bg-green-50 border border-green-200 rounded-lg p-2.5 xl:p-4 mb-0">
          <div class="flex items-start">
            <svg class="w-8 h-8 text-green-600 mr-4 flex-shrink-0 mt-1" fill="none" stroke="currentColor"
              viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <h2 class="font-header text-[1.5rem] font-bold text-green-800 mb-1">
                Application Submitted Successfully!
              </h2>
              <p class="font-body text-[1.125rem] text-gray-700 leading-[1.75]">
                Thank you for your interest. Our team will review your application and get back to you soon.
              </p>
            </div>
          </div>
        </div>

        <div class="space-y-1.5">
          <div class="bg-white p-2.5 xl:p-4 rounded-lg">
            <h2 class="font-header text-[1.5rem] xl:text-[1.5rem] font-bold text-primary mb-1.5">Open Positions</h2>
            <div v-if="loading" class="text-center py-1">
              <Loader size="large" />
            </div>
            <div v-else-if="careers.length === 0" class="space-y-1">
              <div class="border-l-4 border-gray-300 pl-4">
                <h3 class="font-header text-[1.125rem] font-semibold text-primary mb-1">No positions available at this
                  time
                </h3>
                <p class="font-body text-[1.125rem] text-gray-900 leading-[1.75]">
                  We are not currently hiring, but we would love to hear from you.
                  <button @click="showApplicationForm(null)"
                    class="text-green-700 hover:text-green-800 font-sans underline">Submit an application</button>
                  and we will keep your information on file for future opportunities.
                </p>
              </div>
            </div>
            <div v-else class="space-y-0.5">
              <div v-for="career in careers" :key="career.id" class="border-b border-gray-200 pb-0.5">
                <div class="flex justify-between items-start">
                  <div class="flex-1">
                    <a :href="`/careers/${career.id}`" target="_blank"
                      class="font-header text-[1.125rem] font-semibold text-primary hover:text-green-700 transition-colors">
                      {{ career.title }}
                    </a>
                    <p v-if="career.location" class="font-body text-[1.125rem] text-gray-600 leading-[1.75] mt-0.5">{{
                      career.location }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="showForm" class="bg-white rounded-lg shadow-lg p-2 xl:p-3">
            <div class="flex justify-between items-center mb-1">
              <h2 class="font-header text-[1.5rem] font-bold text-primary">
                Apply for {{ selectedCareerTitle || 'Position' }}
              </h2>
              <button @click="closeForm" class="text-gray-400 hover:text-primary transition-colors">
                <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form @submit.prevent="handleSubmit" class="space-y-1">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-1">
                <div>
                  <label for="firstName" class="block text-sm font-header font-semibold text-primary mb-0.5">
                    First Name <span class="text-red-500">*</span>
                  </label>
                  <input type="text" id="firstName" v-model="form.firstName" required
                    class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]"
                    placeholder="John" />
                </div>
                <div>
                  <label for="lastName" class="block text-sm font-header font-semibold text-primary mb-0.5">
                    Last Name <span class="text-red-500">*</span>
                  </label>
                  <input type="text" id="lastName" v-model="form.lastName" required
                    class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]"
                    placeholder="Doe" />
                </div>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-1">
                <div>
                  <label for="email" class="block text-sm font-header font-semibold text-primary mb-0.5">
                    Email <span class="text-red-500">*</span>
                  </label>
                  <input type="email" id="email" v-model="form.email" required
                    class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]"
                    placeholder="your.email@example.com" />
                </div>
                <div>
                  <label for="phone" class="block text-sm font-header font-semibold text-primary mb-0.5">
                    Phone
                  </label>
                  <input type="tel" id="phone" v-model="form.phone" @blur="validatePhone"
                    class="w-full px-4 py-3 bg-white text-black border rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]"
                    :class="phoneError ? 'border-red-500' : 'border-gray-300'" placeholder="+1 (555) 123-4567" />
                  <p v-if="phoneError" class="text-xs text-red-600 mt-0.5 font-body">{{ phoneError }}</p>
                </div>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-1">
                <div>
                  <label for="resumeFile" class="block text-sm font-header font-semibold text-primary mb-0.5">
                    Resume/CV <span class="text-red-500">*</span>
                  </label>
                  <div class="file-drop-zone" :class="{ 'drag-over': isDraggingResume }"
                    @drop.prevent="handleResumeDrop" @dragover.prevent="isDraggingResume = true"
                    @dragleave.prevent="isDraggingResume = false" @click="triggerResumeInput">
                    <input ref="resumeInputRef" type="file" id="resumeFile" @change="handleResumeSelect"
                      accept=".pdf,.doc,.docx" required class="hidden" />
                    <div v-if="form.resumeFileName" class="file-selected-content">
                      <div class="w-16 h-16 rounded-full bg-[#2D4D31] flex items-center justify-center mb-3">
                        <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <p class="text-sm font-sans text-[#111111] mb-2 break-words max-w-full">
                        {{ form.resumeFileName.length > 30 ? form.resumeFileName.substring(0, 30) + '...' :
                          form.resumeFileName }}
                      </p>
                      <button type="button" @click.stop="removeResume"
                        class="text-sm font-sans text-[#7C898B] uppercase hover:text-[#253439] transition-colors">
                        REMOVE
                      </button>
                    </div>
                    <div v-else class="file-drop-content">
                      <svg class="w-16 h-16 text-[#DDC1A7] mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <p class="text-sm font-sans text-[#111111] mb-1">Drop Resume Here</p>
                      <p class="text-xs font-sans text-[#111111] mb-2">or</p>
                      <p class="text-sm font-sans font-bold text-[#B29E84]">Upload Resume</p>
                    </div>
                    <p v-if="uploadingResume"
                      class="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 rounded-xl text-sm text-blue-600 font-sans">
                      Uploading resume...
                    </p>
                  </div>
                  <p class="text-xs text-gray-500 mt-0.5 font-body">
                    Accepted formats: PDF, DOC, DOCX (Max 10MB)
                  </p>
                </div>
                <div>
                  <label for="coverLetterFile" class="block text-sm font-header font-semibold text-primary mb-0.5">
                    Cover Letter
                  </label>
                  <div class="file-drop-zone" :class="{ 'drag-over': isDraggingCoverLetter }"
                    @drop.prevent="handleCoverLetterDrop" @dragover.prevent="isDraggingCoverLetter = true"
                    @dragleave.prevent="isDraggingCoverLetter = false" @click="triggerCoverLetterInput">
                    <input ref="coverLetterInputRef" type="file" id="coverLetterFile" @change="handleCoverLetterSelect"
                      accept=".pdf,.doc,.docx" class="hidden" />
                    <div v-if="form.coverLetterFileName" class="file-selected-content">
                      <div class="w-16 h-16 rounded-full bg-[#2D4D31] flex items-center justify-center mb-3">
                        <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <p class="text-sm font-sans text-[#111111] mb-2 break-words max-w-full">
                        {{ form.coverLetterFileName.length > 30 ? form.coverLetterFileName.substring(0, 30) + '...' :
                          form.coverLetterFileName }}
                      </p>
                      <button type="button" @click.stop="removeCoverLetter"
                        class="text-sm font-sans text-[#7C898B] uppercase hover:text-[#253439] transition-colors">
                        REMOVE
                      </button>
                    </div>
                    <div v-else class="file-drop-content">
                      <svg class="w-16 h-16 text-[#DDC1A7] mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <p class="text-sm font-sans text-[#111111] mb-1">Drop Cover Letter Here</p>
                      <p class="text-xs font-sans text-[#111111] mb-2">or</p>
                      <p class="text-sm font-sans font-bold text-[#B29E84]">Upload Cover Letter</p>
                    </div>
                    <p v-if="uploadingCoverLetter"
                      class="absolute inset-0 flex items-center justify-center bg-white bg-opacity-90 rounded-xl text-sm text-blue-600 font-sans">
                      Uploading cover letter...
                    </p>
                  </div>
                  <p class="text-xs text-gray-500 mt-0.5 font-body">
                    Accepted formats: PDF, DOC, DOCX (Max 10MB)
                  </p>
                </div>
              </div>
              <div>
                <label for="coverLetter" class="block text-sm font-header font-semibold text-primary mb-0.5">
                  Cover Letter Text (Optional)
                </label>
                <textarea id="coverLetter" v-model="form.coverLetter" rows="8"
                  class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75] resize-none"
                  placeholder="Tell us why you are interested in this position and what makes you a great fit..."></textarea>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-1">
                <div>
                  <label for="linkedinUrl" class="block text-sm font-header font-semibold text-primary mb-0.5">
                    LinkedIn Profile
                  </label>
                  <input type="url" id="linkedinUrl" v-model="form.linkedinUrl"
                    class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]"
                    placeholder="https://linkedin.com/in/yourprofile" />
                </div>
                <div>
                  <label for="portfolioUrl" class="block text-sm font-header font-semibold text-primary mb-0.5">
                    Portfolio/Website
                  </label>
                  <input type="url" id="portfolioUrl" v-model="form.portfolioUrl"
                    class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]"
                    placeholder="https://yourportfolio.com" />
                </div>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-1">
                <div>
                  <label for="yearsOfExperience" class="block text-sm font-header font-semibold text-primary mb-0.5">
                    Years of Experience
                  </label>
                  <input type="number" id="yearsOfExperience" v-model.number="form.yearsOfExperience" min="0"
                    class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]"
                    placeholder="5" />
                </div>
                <div>
                  <label for="currentPosition" class="block text-sm font-header font-semibold text-primary mb-0.5">
                    Current Position
                  </label>
                  <input type="text" id="currentPosition" v-model="form.currentPosition"
                    class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]"
                    placeholder="Senior Editor" />
                </div>
              </div>
              <div>
                <label for="availabilityDate" class="block text-sm font-header font-semibold text-primary mb-0.5">
                  Availability Date
                </label>
                <input type="date" id="availabilityDate" v-model="form.availabilityDate"
                  class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75]" />
              </div>
              <div>
                <label for="additionalInfo" class="block text-sm font-header font-semibold text-primary mb-0.5">
                  Additional Information
                </label>
                <textarea id="additionalInfo" v-model="form.additionalInfo" rows="4"
                  class="w-full px-4 py-3 bg-white text-black border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-black focus:border-black font-body text-[1.125rem] leading-[1.75] resize-none"
                  placeholder="Any additional information you would like to share..."></textarea>
              </div>
              <div class="flex gap-4 pt-0.5">
                <button type="submit" :disabled="submitting || !form.resumeFileUrl"
                  class="flex-1 bg-green-700 text-white px-8 py-3 rounded-md font-header font-semibold hover:bg-green-800 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed">
                  {{ submitting ? 'Submitting...' : 'Submit Application' }}
                </button>
                <button type="button" @click="closeForm"
                  class="px-8 py-3 bg-white text-green-700 border border-green-700 rounded-md font-header font-semibold hover:bg-green-50 transition-colors">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { publicService } from '../services/public'
import { API_BASE_URL } from '../config.js'
import { usePageTranslation } from '../composables/usePageTranslation'

const componentRef = ref(null)
usePageTranslation(componentRef)
import axios from 'axios'
import Loader from './Loader.vue'
import { useLoadingState } from '../composables/useLoadingState'
import { validatePhoneNumber } from '../utils/phoneValidation'

const careers = ref([])
const searchQuery = ref('')
const isSearching = ref(false)
const { loading, setLoading } = useLoadingState(true)
const showForm = ref(false)
const selectedCareerId = ref(null)
const selectedCareerTitle = ref('')
const submitting = ref(false)
const submissionSuccess = ref(false)
const uploadingResume = ref(false)
const uploadingCoverLetter = ref(false)
const phoneError = ref(null)
const pageDescription = ref('Join our hive and help shape the future of publishing.')
const pageSections = ref([])
const isDraggingResume = ref(false)
const isDraggingCoverLetter = ref(false)
const resumeInputRef = ref(null)
const coverLetterInputRef = ref(null)

const form = ref({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  coverLetter: '',
  resumeFile: null,
  resumeFileName: '',
  resumeFileUrl: '',
  coverLetterFile: null,
  coverLetterFileName: '',
  coverLetterFileUrl: '',
  linkedinUrl: '',
  portfolioUrl: '',
  yearsOfExperience: null,
  currentPosition: '',
  availabilityDate: '',
  additionalInfo: ''
})

const loadCareers = async () => {
  setLoading(true)
  isSearching.value = false
  searchQuery.value = ''
  try {
    const data = await publicService.getCareers()
    careers.value = Array.isArray(data) ? data : []
  } catch (error) {
    console.error('Failed to load careers:', error)
    careers.value = []
  } finally {
    setLoading(false)
  }
}

const handleSearch = async () => {
  if (!searchQuery.value.trim()) {
    loadCareers()
    return
  }

  setLoading(true)
  isSearching.value = true
  try {
    const data = await publicService.searchCareers(searchQuery.value.trim())
    careers.value = Array.isArray(data?.data) ? data.data : []
  } catch (error) {
    console.error('Failed to search careers:', error)
    careers.value = []
  } finally {
    setLoading(false)
  }
}

const clearSearch = () => {
  searchQuery.value = ''
  loadCareers()
}

const loadPageDescription = async () => {
  try {
    const page = await publicService.getPage('Careers')
    if (page?.description) pageDescription.value = page.description
    pageSections.value = Array.isArray(page?.sections) ? page.sections : []
  } catch (error) {
    console.error('Failed to load page description:', error)
  }
}

const showApplicationForm = async (careerId) => {
  selectedCareerId.value = careerId
  selectedCareerTitle.value = ''

  if (careerId) {
    const career = careers.value.find(c => c.id === careerId)
    if (career) {
      selectedCareerTitle.value = career.title
    }
  }

  showForm.value = true
  submissionSuccess.value = false
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

const closeForm = () => {
  showForm.value = false
  selectedCareerId.value = null
  selectedCareerTitle.value = ''
  isDraggingResume.value = false
  isDraggingCoverLetter.value = false
  form.value = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    coverLetter: '',
    resumeFile: null,
    resumeFileName: '',
    resumeFileUrl: '',
    coverLetterFile: null,
    coverLetterFileName: '',
    coverLetterFileUrl: '',
    linkedinUrl: '',
    portfolioUrl: '',
    yearsOfExperience: null,
    currentPosition: '',
    availabilityDate: '',
    additionalInfo: ''
  }
}

const triggerResumeInput = () => {
  resumeInputRef.value?.click()
}

const triggerCoverLetterInput = () => {
  coverLetterInputRef.value?.click()
}

const handleResumeDrop = async (event) => {
  isDraggingResume.value = false
  const file = event.dataTransfer.files[0]
  if (!file) return
  await uploadResume(file)
}

const handleCoverLetterDrop = async (event) => {
  isDraggingCoverLetter.value = false
  const file = event.dataTransfer.files[0]
  if (!file) return
  await uploadCoverLetter(file)
}

const handleResumeSelect = async (event) => {
  const file = event.target.files[0]
  if (!file) return
  await uploadResume(file)
}

const handleCoverLetterSelect = async (event) => {
  const file = event.target.files[0]
  if (!file) return
  await uploadCoverLetter(file)
}

const uploadResume = async (file) => {
  if (file.size > 10 * 1024 * 1024) {
    alert('File size must be less than 10MB')
    return
  }

  form.value.resumeFile = file
  form.value.resumeFileName = file.name
  uploadingResume.value = true

  try {
    const formData = new FormData()
    formData.append('file', file)

    const response = await axios.post(`${API_BASE_URL}/api/upload/resume`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })

    if (response.data && response.data.url) {
      form.value.resumeFileUrl = response.data.url
    } else {
      throw new Error('No URL returned from upload')
    }
  } catch (error) {
    console.error('Failed to upload resume:', error)
    alert('Failed to upload resume. Please try again.')
    form.value.resumeFile = null
    form.value.resumeFileName = ''
    form.value.resumeFileUrl = ''
  } finally {
    uploadingResume.value = false
  }
}

const uploadCoverLetter = async (file) => {
  if (file.size > 10 * 1024 * 1024) {
    alert('File size must be less than 10MB')
    return
  }

  form.value.coverLetterFile = file
  form.value.coverLetterFileName = file.name
  uploadingCoverLetter.value = true

  try {
    const formData = new FormData()
    formData.append('file', file)

    const response = await axios.post(`${API_BASE_URL}/api/upload/resume`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })

    if (response.data && response.data.url) {
      form.value.coverLetterFileUrl = response.data.url
    } else {
      throw new Error('No URL returned from upload')
    }
  } catch (error) {
    console.error('Failed to upload cover letter:', error)
    alert('Failed to upload cover letter. Please try again.')
    form.value.coverLetterFile = null
    form.value.coverLetterFileName = ''
    form.value.coverLetterFileUrl = ''
  } finally {
    uploadingCoverLetter.value = false
  }
}

const removeResume = () => {
  form.value.resumeFile = null
  form.value.resumeFileName = ''
  form.value.resumeFileUrl = ''
  if (resumeInputRef.value) {
    resumeInputRef.value.value = ''
  }
}

const removeCoverLetter = () => {
  form.value.coverLetterFile = null
  form.value.coverLetterFileName = ''
  form.value.coverLetterFileUrl = ''
  if (coverLetterInputRef.value) {
    coverLetterInputRef.value.value = ''
  }
}

const validatePhone = () => {
  if (!form.value.phone || form.value.phone.trim() === '') {
    phoneError.value = null
    return true
  }
  const validation = validatePhoneNumber(form.value.phone)
  phoneError.value = validation.error
  return validation.isValid
}

const handleSubmit = async () => {
  if (!form.value.resumeFileUrl) {
    alert('Please upload your resume/CV')
    return
  }

  if (!validatePhone()) {
    alert(phoneError.value || 'Please enter a valid phone number')
    return
  }

  submitting.value = true

  try {
    const applicationData = {
      career_id: selectedCareerId.value,
      first_name: form.value.firstName,
      last_name: form.value.lastName,
      email: form.value.email || null,
      phone: form.value.phone || null,
      cover_letter: form.value.coverLetter || null,
      resume_file_url: form.value.resumeFileUrl,
      cover_letter_file_url: form.value.coverLetterFileUrl || null,
      linkedin_url: form.value.linkedinUrl || null,
      portfolio_url: form.value.portfolioUrl || null,
      years_of_experience: form.value.yearsOfExperience || null,
      current_position: form.value.currentPosition || null,
      availability_date: form.value.availabilityDate || null,
      additional_info: form.value.additionalInfo || null
    }

    const response = await axios.post(`${API_BASE_URL}/api/career-applications`, applicationData)

    if (response.data) {
      submissionSuccess.value = true
      showForm.value = false
      window.scrollTo({ top: 0, behavior: 'smooth' })
      setTimeout(() => {
        closeForm()
      }, 5000)
    }
  } catch (error) {
    console.error('Failed to submit application:', error)
    alert('Failed to submit application. Please try again.')
  } finally {
    submitting.value = false
  }
}

onMounted(() => {
  loadCareers()
  loadPageDescription()
})
</script>

<style scoped>
/* Comprehensive Quill Editor Content Rendering */
:deep(h1) {
  font-size: 2.5rem;
  font-weight: bold;
  color: #111111;
  margin-top: 1.5rem;
  margin-bottom: 1rem;
}

:deep(h2) {
  font-size: 2rem;
  font-weight: bold;
  color: #111111;
  margin-top: 1.25rem;
  margin-bottom: 0.75rem;
}

:deep(h3) {
  font-size: 1.5rem;
  font-weight: bold;
  color: #111111;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
}

/* Font Sizes */
:deep(.ql-size-small) {
  font-size: 0.75rem;
}

:deep(.ql-size-large) {
  font-size: 1.5rem;
}

:deep(.ql-size-huge) {
  font-size: 2.5rem;
}

/* Blockquotes */
:deep(blockquote) {
  border-left: 4px solid #ccc;
  padding-left: 1rem;
  margin: 1rem 0;
  font-style: italic;
  color: #666;
}

/* Code Blocks */
:deep(pre) {
  background-color: #f4f4f4;
  padding: 1rem;
  border-radius: 4px;
  overflow-x: auto;
  margin: 1rem 0;
}

:deep(code) {
  background-color: #f4f4f4;
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
  font-family: 'Courier New', monospace;
}

:deep(pre code) {
  background-color: transparent;
  padding: 0;
}

/* Images */
:deep(img) {
  max-width: 100%;
  height: auto;
  margin: 1rem 0;
}

/* Videos and Iframes */
:deep(iframe),
:deep(video) {
  max-width: 100%;
  height: auto;
  margin: 1rem 0;
}

/* Text Alignment */
:deep(.ql-align-center) {
  text-align: center;
}

:deep(.ql-align-right) {
  text-align: right;
}

:deep(.ql-align-justify) {
  text-align: justify;
}

:deep([style*='text-align: center']) {
  text-align: center;
}

:deep([style*='text-align: right']) {
  text-align: right;
}

:deep([style*='text-align: justify']) {
  text-align: justify;
}

/* Font Families */
:deep(.ql-font-serif) {
  font-family: Georgia, 'Times New Roman', serif;
}

:deep(.ql-font-monospace) {
  font-family: 'Courier New', Courier, monospace;
}

/* Lists */
:deep(ul),
:deep(ol) {
  margin: 1rem 0;
  padding-left: 1.5rem;
}

:deep(li) {
  margin-bottom: 0.5rem;
}

/* Links */
:deep(a) {
  color: #2563eb;
  text-decoration: underline;
  transition: color 0.2s;
}

:deep(a:hover) {
  color: #1e40af;
}

/* Spacing */
:deep(p) {
  margin-bottom: 1rem;
}

/* File Drop Zone */
.file-drop-zone {
  border: 2px dashed #DDC1A7;
  border-radius: 0.75rem;
  background: #FFFFFF;
  padding: 3rem 2rem;
  cursor: pointer;
  position: relative;
  transition: all 0.2s;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.file-drop-zone:hover {
  border-color: #B29E84;
}

.file-drop-zone.drag-over {
  border-color: #B29E84;
  background: #FAFAFA;
}

.file-drop-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.file-selected-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
}
</style>
